/**
 * presence.ts — Hằng số + hàm thuần cho Trạng thái trực tuyến (kiểu SaleSmartly).
 * Nhãn là getter đọc i18n lúc dùng → luôn theo ngôn ngữ đang chọn.
 */
import { i18n } from '@/lib/i18n'

export type PresenceStatus = 'online' | 'busy' | 'offline'

const option = (id: PresenceStatus) => ({
  id,
  get label() { return i18n.t(`common:presence.${id}`) },
  get hint() { return i18n.t(`common:presence.${id}Hint`) },
})

export const PRESENCE_OPTIONS: { id: PresenceStatus; label: string; hint: string }[] = [option('online'), option('busy'), option('offline')]

export const PRESENCE_LABEL: Record<PresenceStatus, string> = {
  get online() { return i18n.t('common:presence.online') },
  get busy() { return i18n.t('common:presence.busy') },
  get offline() { return i18n.t('common:presence.offline') },
}

export const PRESENCE_DOT: Record<PresenceStatus, string> = {
  online: 'bg-success',
  busy: 'bg-warning',
  offline: 'bg-muted-foreground/40',
}

export const isPresenceStatus = (v: unknown): v is PresenceStatus =>
  v === 'online' || v === 'busy' || v === 'offline'

/** Trạng thái của một người trong bảng; chưa từng có bản ghi = Ngoại tuyến. */
export function presenceOf(map: Record<string, PresenceStatus> | undefined, userId: string | undefined): PresenceStatus {
  if (!map || !userId) return 'offline'
  return map[userId] ?? 'offline'
}

/** Áp sự kiện socket `presence:changed` vào bảng (trả bảng mới — bất biến cho React Query). */
export function applyPresenceEvent(
  map: Record<string, PresenceStatus> | undefined,
  ev: { userId?: unknown; status?: unknown },
): Record<string, PresenceStatus> | undefined {
  if (typeof ev.userId !== 'string' || !isPresenceStatus(ev.status)) return map
  if (map?.[ev.userId] === ev.status) return map
  return { ...(map ?? {}), [ev.userId]: ev.status }
}

/** 3725 → "1 giờ 2 phút"; khớp định dạng phía máy chủ. */
export function formatDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec) || sec < 0) return '—'
  const s = Math.floor(sec)
  const d = Math.floor(s / 86_400)
  const h = Math.floor((s % 86_400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  if (d > 0) return h > 0 ? i18n.t('sharedMisc:presence.duration.daysHours', { d, h }) : i18n.t('sharedMisc:presence.duration.days', { d })
  if (h > 0) return m > 0 ? i18n.t('sharedMisc:presence.duration.hoursMinutes', { h, m }) : i18n.t('sharedMisc:presence.duration.hours', { h })
  if (m > 0) return r > 0 ? i18n.t('sharedMisc:presence.duration.minutesSeconds', { m, r }) : i18n.t('sharedMisc:presence.duration.minutes', { m })
  return i18n.t('sharedMisc:presence.duration.seconds', { r })
}
